import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { tenantsAPI, storesAPI, devicesAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Building2, Store, Wifi, WifiOff, RefreshCw, Play, Square,
  Activity, Clock, Calendar, ChevronRight, BarChart2, RotateCw,
} from 'lucide-react';
import { API_BASE } from '../services/apiBase';

const API    = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
}

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function uptime(device) {
  const iso = device?.extra?.session_started_at || device?.paired_at;
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  const days = Math.floor(diff / 86400);
  const h = Math.floor((diff % 86400) / 3600);
  const m = Math.floor((diff % 3600) / 60);
  if (days > 0) return `${days}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function isOnline(device) {
  if (!device.last_heartbeat_at) return false;
  return (Date.now() - new Date(device.last_heartbeat_at)) < 120000;
}

function fmtDuration(s) {
  if (!s) return '--:--';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── Now Playing hero card ────────────────────────────────────────────────────
function NowPlayingCard({ device }) {
  const extra = device?.extra || {};
  const track = extra.current_track || '';
  const vol   = extra.volume;
  const parts = track.split(' - ');
  const artist = parts.length > 1 ? parts[0] : null;
  const title  = parts.length > 1 ? parts.slice(1).join(' - ') : track;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #161b22 0%, #1c2333 100%)',
      border: '1px solid #30363d',
      borderRadius: 16, padding: 24, marginBottom: 20,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Glow effect */}
      <div style={{
        position: 'absolute', top: -40, right: -40,
        width: 180, height: 180, borderRadius: '50%',
        background: 'rgba(47,129,247,0.08)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 14 }}>
        NOW PLAYING
      </div>

      {device ? (
        <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          {/* Album art placeholder */}
          <div style={{
            width: 64, height: 64, borderRadius: 10, flexShrink: 0,
            background: device.is_playing
              ? 'linear-gradient(135deg, #2f81f7, #58a6ff)'
              : '#21262d',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: device.is_playing ? '0 0 20px rgba(47,129,247,0.4)' : 'none',
          }}>
            {device.is_playing
              ? <Play size={24} color="#fff" style={{ marginLeft: 3 }} />
              : <Square size={20} color="#484f58" />}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {track ? (
              <>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#e6edf3', lineHeight: '22px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {title || 'Unknown title'}
                </div>
                {artist && (
                  <div style={{ fontSize: 13, color: '#8b949e', marginTop: 3 }}>{artist}</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 15, color: '#484f58', fontStyle: 'italic' }}>
                {device.is_playing ? 'Playing…' : 'Stopped'}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#58a6ff', fontWeight: 600 }}>{device.store_name}</span>
              {vol !== undefined && (
                <span style={{ fontSize: 11, color: '#8b949e' }}>Vol {vol}%</span>
              )}
              <span style={{ fontSize: 11, color: '#484f58' }}>Up {uptime(device)}</span>
            </div>
          </div>

          {/* Live indicator */}
          {device.is_playing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>LIVE</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: '#484f58', fontSize: 14, fontStyle: 'italic' }}>No devices playing</div>
      )}
    </div>
  );
}

// ── Device chip (compact row) ─────────────────────────────────────────────────
function DeviceRow({ device }) {
  const online = isOnline(device);
  const extra  = device.extra || {};
  const track  = extra.current_track || '';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderBottom: '1px solid #21262d',
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
        background: online ? (device.is_playing ? '#22c55e' : '#58a6ff') : '#484f58',
        boxShadow: online && device.is_playing ? '0 0 6px #22c55e' : 'none',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3' }}>{device.store_name}</div>
        {online && track ? (
          <div style={{ fontSize: 11, color: '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{track}</div>
        ) : (
          <div style={{ fontSize: 11, color: '#484f58' }}>{online ? 'Online · idle' : `Last seen ${timeAgo(device.last_heartbeat_at) || 'never'}`}</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: online ? '#22c55e' : '#484f58', flexShrink: 0 }}>
        {online ? (device.is_playing ? 'Playing' : 'Idle') : 'Offline'}
      </div>
    </div>
  );
}

// ── Quick Actions panel ───────────────────────────────────────────────────────
function QuickActions({ navigate }) {
  const actions = [
    { label: 'Broadcast Scheduler', sub: 'Generate queue & manage tracks', icon: <Clock size={16} color="#58a6ff" />, to: '/schedule', accent: '#2f81f7' },
    { label: 'Rotation Manager', sub: 'Categories, clocks & schedules', icon: <RotateCw size={16} color="#a78bfa" />, to: '/rotation-manager', accent: '#7c3aed' },
    { label: 'Spin Report', sub: 'Analyse playout history', icon: <BarChart2 size={16} color="#34d399" />, to: '/rotation-manager', accent: '#059669' },
    { label: 'Music Library', sub: 'Upload & manage tracks', icon: <Activity size={16} color="#fb923c" />, to: '/music-library', accent: '#ea580c' },
  ];
  return (
    <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em' }}>
        QUICK ACTIONS
      </div>
      {actions.map(a => (
        <button key={a.to + a.label} onClick={() => navigate(a.to)} style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '11px 16px', background: 'transparent', border: 'none',
          borderBottom: '1px solid #21262d', cursor: 'pointer', textAlign: 'left',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#1c2333'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: a.accent + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {a.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3' }}>{a.label}</div>
            <div style={{ fontSize: 11, color: '#8b949e' }}>{a.sub}</div>
          </div>
          <ChevronRight size={14} color="#484f58" />
        </button>
      ))}
    </div>
  );
}

// ── Next Up queue panel ───────────────────────────────────────────────────────
function NextUpPanel({ queue, loading }) {
  return (
    <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em' }}>NEXT UP</span>
        {loading && <span style={{ fontSize: 11, color: '#484f58' }}>loading…</span>}
      </div>
      {queue.length === 0 && !loading ? (
        <div style={{ padding: '16px', fontSize: 12, color: '#484f58', textAlign: 'center' }}>
          No active schedule — open Scheduler to generate a queue
        </div>
      ) : (
        queue.slice(0, 6).map((item, i) => {
          const burnColor = { clean: '#22c55e', warning: '#f59e0b', forced: '#ef4444' }[item.burn_status] || '#22c55e';
          return (
            <div key={item.position} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 14px', borderBottom: '1px solid #21262d',
            }}>
              <span style={{ fontSize: 11, color: '#484f58', width: 18, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {i === 0 ? '▶' : i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: i === 0 ? '#e6edf3' : '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.track?.title || item.label || 'Empty slot'}
                </div>
                {item.track?.artist && (
                  <div style={{ fontSize: 11, color: '#484f58', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.track.artist}
                  </div>
                )}
              </div>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: burnColor, flexShrink: 0 }} title={item.burn_status} />
              <span style={{ fontSize: 11, color: '#484f58', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {fmtDuration(item.track?.duration_seconds)}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Today's schedule timeline ─────────────────────────────────────────────────
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function TodaySchedule({ schedules }) {
  const todayNum = new Date().getDay() === 0 ? 7 : new Date().getDay(); // 1=Mon…7=Sun
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();

  const toMin = t => {
    if (!t) return 0;
    const [h, m] = t.slice(0, 5).split(':').map(Number);
    return h * 60 + (m || 0);
  };

  const todaySlots = (schedules || [])
    .filter(s => (s.days_of_week || []).map(Number).includes(todayNum))
    .sort((a, b) => toMin(a.start_time) - toMin(b.start_time));

  return (
    <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em' }}>TODAY'S SCHEDULE</span>
        <span style={{ fontSize: 11, color: '#484f58' }}>{DAYS[todayNum - 1]}</span>
      </div>
      {todaySlots.length === 0 ? (
        <div style={{ padding: 16, fontSize: 12, color: '#484f58', textAlign: 'center' }}>No schedules for today</div>
      ) : (
        todaySlots.map(s => {
          const start = toMin(s.start_time);
          const end   = toMin(s.end_time);
          const active = nowMin >= start && nowMin <= end;
          return (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 14px', borderBottom: '1px solid #21262d',
              background: active ? 'rgba(47,129,247,0.06)' : 'transparent',
            }}>
              <div style={{ width: 3, height: 32, borderRadius: 2, background: active ? '#2f81f7' : '#30363d', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: active ? '#58a6ff' : '#8b949e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </div>
                <div style={{ fontSize: 11, color: '#484f58' }}>
                  {s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)}
                </div>
              </div>
              {active && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#2f81f7', letterSpacing: '0.05em', flexShrink: 0 }}>NOW</span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allDevices, setAllDevices]   = useState([]);
  const [storeMap, setStoreMap]       = useState({});
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [queue, setQueue]             = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [schedules, setSchedules]     = useState([]);
  const [tenantCount, setTenantCount] = useState(0);

  const loadAll = useCallback(async () => {
    try {
      const tenantsRes = await tenantsAPI.list();
      const tenantList = tenantsRes.data || [];
      setTenantCount(tenantList.length);
      const storeMapTemp = {};
      const deviceList   = [];
      await Promise.all(tenantList.map(async (tenant) => {
        try {
          const storesRes = await storesAPI.list(tenant.id);
          const stores = storesRes.data || [];
          await Promise.all(stores.map(async (store) => {
            storeMapTemp[store.id] = store;
            try {
              const devRes = await devicesAPI.listByStore(store.id);
              (devRes.data || []).forEach(d => deviceList.push({ ...d, store_name: store.name, tenant_name: tenant.name }));
            } catch {}
          }));
        } catch {}
      }));
      setStoreMap(storeMapTemp);
      setAllDevices(deviceList);
      setLastRefresh(new Date());
    } catch {}
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  const loadQueue = useCallback(async (storeId) => {
    setQueueLoading(true);
    try {
      const params = new URLSearchParams({ tenant_id: TENANT, size: 8 });
      if (storeId) params.append('store_id', storeId);
      const r = await axios.get(`${API}/broadcast-queue?${params}`, { headers: authHeaders() });
      setQueue(r.data.queue || []);
    } catch {}
    setQueueLoading(false);
  }, []);

  const loadSchedules = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/rotations/schedules`, {
        params: { tenant_id: TENANT },
        headers: authHeaders(),
      });
      setSchedules(r.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadAll();
    loadSchedules();
    loadQueue('');
    const interval = setInterval(loadAll, 60000);
    return () => clearInterval(interval);
  }, [loadAll, loadSchedules, loadQueue]);

  const refresh = () => { setRefreshing(true); loadAll(); loadQueue(''); loadSchedules(); };

  const onlineDevices  = allDevices.filter(isOnline);
  const offlineDevices = allDevices.filter(d => !isOnline(d));
  const playingDevices = onlineDevices.filter(d => d.is_playing);
  const heroDevice     = playingDevices[0] || onlineDevices[0] || null;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3' }}>

      {/* Page header */}
      <div style={{ padding: '20px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e6edf3' }}>
            {greeting}{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: '#8b949e' }}>
            Live platform overview · auto-refreshes every 60s
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && <span style={{ fontSize: 12, color: '#484f58' }}>Updated {timeAgo(lastRefresh)}</span>}
          <button
            onClick={refresh}
            disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #30363d', background: '#161b22', color: '#8b949e', cursor: 'pointer', fontSize: 13 }}
          >
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, padding: '20px 28px' }}>
        {[
          { label: 'Active Brands',  value: tenantCount,           color: '#58a6ff', icon: <Building2 size={18} color="#2f81f7" /> },
          { label: 'Total Stores',   value: Object.keys(storeMap).length, color: '#22c55e', icon: <Store size={18} color="#22c55e" /> },
          { label: 'Online Devices', value: `${onlineDevices.length} / ${allDevices.length}`, color: '#22c55e', icon: <Wifi size={18} color="#22c55e" /> },
          { label: 'Now Playing',    value: playingDevices.length, color: '#f472b6', icon: <Activity size={18} color="#ec4899" /> },
        ].map(s => (
          <div key={s.label} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em', marginBottom: 6 }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: '#1c2333' }}>{s.icon}</div>
          </div>
        ))}
      </div>

      {/* Main two-column layout */}
      <div style={{ display: 'flex', gap: 20, padding: '0 28px 28px', alignItems: 'flex-start' }}>

        {/* Left column: Now Playing + Device list */}
        <div style={{ flex: '1 1 0', minWidth: 0 }}>

          {/* Now Playing hero */}
          <NowPlayingCard device={heroDevice} />

          {/* Devices section */}
          <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#8b949e', letterSpacing: '0.08em' }}>
                ALL DEVICES
              </span>
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#484f58' }}>
                {onlineDevices.length > 0 && <span style={{ color: '#22c55e' }}>● {onlineDevices.length} online</span>}
                {offlineDevices.length > 0 && <span>● {offlineDevices.length} offline</span>}
              </div>
            </div>
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#484f58' }}>Loading…</div>
            ) : allDevices.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#484f58' }}>
                <WifiOff size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 13 }}>No devices paired yet</div>
              </div>
            ) : (
              <>
                {onlineDevices.map(d => <DeviceRow key={d.id} device={d} />)}
                {offlineDevices.map(d => <DeviceRow key={d.id} device={d} />)}
              </>
            )}
          </div>
        </div>

        {/* Right column: Quick Actions + Next Up + Schedule */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <QuickActions navigate={navigate} />
          <NextUpPanel queue={queue} loading={queueLoading} />
          <TodaySchedule schedules={schedules} />
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
